// Push Notifications Handler

class PushNotificationManager {
  constructor() {
    this.registration = null;
    this.vapidPublicKey = null;
    this.init();
  }

  async init() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('Push notifications are not supported');
      return;
    }

    // Detect iOS and show appropriate message
    this.detectAndUpdateUIForIOS();

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service Worker registered successfully');

      // Get VAPID public key from server
      const response = await fetch('/push_subscriptions/vapid_public_key');
      const data = await response.json();
      this.vapidPublicKey = data.public_key;

      // Check if already subscribed
      const existingSubscription = await this.registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('Found existing subscription');

        // Verify subscription is valid on server
        const isValid = await this.verifySubscription(existingSubscription);

        if (isValid) {
          console.log('Subscription is valid');
          this.updateUI(true);
        } else {
          console.log('Subscription is invalid, unsubscribing...');
          await existingSubscription.unsubscribe();
          this.updateUI(false);
        }
      } else {
        console.log('No existing subscription');
        this.updateUI(false);
      }
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }

  detectAndUpdateUIForIOS() {
    // Detect iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator.standalone);

    if (isIOS) {
      const defaultMessage = document.getElementById('notification-banner-message');
      const iosMessage = document.getElementById('notification-banner-ios-message');

      if (defaultMessage && iosMessage) {
        if (!isInStandaloneMode) {
          // Not installed as PWA - show installation instructions
          defaultMessage.style.display = 'none';
          iosMessage.style.display = 'inline';
        } else {
          // Already installed as PWA - show normal message
          defaultMessage.style.display = 'inline';
          iosMessage.style.display = 'none';
        }
      }
    }
  }

  async verifySubscription(subscription) {
    // Check if subscription exists on server
    try {
      const subscriptionJson = subscription.toJSON();
      const response = await fetch('/push_subscriptions/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          endpoint: subscriptionJson.endpoint
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.valid;
      }
      return false;
    } catch (e) {
      console.error('Error verifying subscription:', e);
      return false;
    }
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  async subscribe() {
    if (!this.registration || !this.vapidPublicKey) {
      console.error('Service Worker or VAPID key not available');
      return;
    }

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return;
      }

      // Subscribe to push notifications
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
      });

      // Send subscription to server
      await this.sendSubscriptionToServer(subscription);

      this.updateUI(true);
      console.log('Successfully subscribed to push notifications');
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
    }
  }

  async unsubscribe() {
    try {
      const subscription = await this.registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await this.removeSubscriptionFromServer(subscription);
        this.updateUI(false);
        console.log('Successfully unsubscribed from push notifications');
      }
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
    }
  }

  async sendSubscriptionToServer(subscription) {
    const subscriptionJson = subscription.toJSON();

    const response = await fetch('/push_subscriptions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        subscription: {
          endpoint: subscriptionJson.endpoint,
          p256dh_key: subscriptionJson.keys.p256dh,
          auth_key: subscriptionJson.keys.auth
        }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to send subscription to server');
    }
  }

  async removeSubscriptionFromServer(subscription) {
    const subscriptionJson = subscription.toJSON();

    await fetch(`/push_subscriptions/${encodeURIComponent(subscriptionJson.endpoint)}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    });
  }

  updateUI(isSubscribed) {
    const notificationButton = document.getElementById('notification-toggle-btn');
    const notificationBanner = document.getElementById('notification-banner');

    if (notificationButton) {
      if (isSubscribed) {
        notificationButton.textContent = 'Disable Notifications';
        notificationButton.classList.remove('btn-primary');
        notificationButton.classList.add('btn-secondary');
      } else {
        notificationButton.textContent = 'Enable Notifications';
        notificationButton.classList.remove('btn-secondary');
        notificationButton.classList.add('btn-primary');
      }
    }

    if (notificationBanner) {
      // Use user-specific localStorage key
      const userId = window.currentUserId;
      const storageKey = userId ? `notificationBannerDismissed_${userId}` : 'notificationBannerDismissed';

      if (isSubscribed) {
        notificationBanner.style.display = 'none';
        localStorage.setItem(storageKey, 'true');
      } else if (localStorage.getItem(storageKey) === 'true') {
        notificationBanner.style.display = 'none';
      } else {
        // Make sure banner is visible if not dismissed
        notificationBanner.style.display = '';
      }
    }
  }

  async toggleSubscription() {
    const subscription = await this.registration.pushManager.getSubscription();

    if (subscription) {
      await this.unsubscribe();
    } else {
      await this.subscribe();
    }
  }
}

// Initialize the push notification manager
let pushManager;

function initializePushNotifications() {
  console.log('Initializing push notifications...');

  if (!pushManager) {
    pushManager = new PushNotificationManager();
  }

  // Handle notification toggle button click
  const notificationButton = document.getElementById('notification-toggle-btn');
  if (notificationButton) {
    console.log('Found notification button, attaching click handler');

    // Remove existing listeners by cloning
    const newButton = notificationButton.cloneNode(true);
    notificationButton.parentNode.replaceChild(newButton, notificationButton);

    newButton.addEventListener('click', async (e) => {
      e.preventDefault();
      console.log('Notification button clicked');
      await pushManager.toggleSubscription();
    });
  } else {
    console.log('Notification button not found');
  }

  // Handle banner dismiss
  const dismissButton = document.getElementById('dismiss-notification-banner');
  if (dismissButton) {
    console.log('Found dismiss button, attaching click handler');

    // Remove existing listeners by cloning
    const newDismissButton = dismissButton.cloneNode(true);
    dismissButton.parentNode.replaceChild(newDismissButton, dismissButton);

    newDismissButton.addEventListener('click', () => {
      console.log('Dismiss button clicked');
      const userId = window.currentUserId;
      const storageKey = userId ? `notificationBannerDismissed_${userId}` : 'notificationBannerDismissed';

      document.getElementById('notification-banner').style.display = 'none';
      localStorage.setItem(storageKey, 'true');
    });
  }

  // Hide banner on load if dismissed for this user
  const notificationBanner = document.getElementById('notification-banner');
  if (notificationBanner) {
    const userId = window.currentUserId;
    const storageKey = userId ? `notificationBannerDismissed_${userId}` : 'notificationBannerDismissed';

    if (localStorage.getItem(storageKey) === 'true') {
      notificationBanner.style.display = 'none';
    }
  }
}

// Listen for both DOMContentLoaded and Turbo events
document.addEventListener('DOMContentLoaded', initializePushNotifications);
document.addEventListener('turbo:load', initializePushNotifications);
document.addEventListener('turbo:render', initializePushNotifications);

// Export for use in other modules
export default PushNotificationManager;
